import React, { useEffect, useRef, useState, useCallback } from 'react';
import { PageContainer } from '../components/Container/PageContainer';
import { Title, Text, Flex, Table, Button, Loader } from '@mantine/core';
import { dbService } from '../firebaseInstance';
import { Image } from '@mantine/core';
import moment from 'moment';

/**
 * Admin 페이지 - 가입자 모니터링
 *
 * 최적화: N+1 리스너 제거 → 일회성 쿼리 + 새로고침 버튼
 */
const Admin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: 'lastVisit',
    order: 'desc',
  });

  const sortConfigRef = useRef(sortConfig);

  useEffect(() => {
    sortConfigRef.current = sortConfig;
  }, [sortConfig]);

  /**
   * 클라이언트 사이드 정렬 (visitLog 배열 기반)
   */
  const applySort = useCallback((list, cfg = sortConfigRef.current) => {
    const { key, order } = cfg;
    return [...list].sort((a, b) => {
      let aValue, bValue;

      if (key === 'lastVisit') {
        aValue = a.visitLog?.length > 0
          ? new Date(a.visitLog[a.visitLog.length - 1])
          : (a.profile?.signUpDate ? new Date(a.profile.signUpDate) : null);
        bValue = b.visitLog?.length > 0
          ? new Date(b.visitLog[b.visitLog.length - 1])
          : (b.profile?.signUpDate ? new Date(b.profile.signUpDate) : null);
      } else if (key === 'signUpDate') {
        aValue = a.profile?.signUpDate ? new Date(a.profile.signUpDate) : null;
        bValue = b.profile?.signUpDate ? new Date(b.profile.signUpDate) : null;
      } else if (key === 'visitCount') {
        aValue = a.visitLog?.length ?? 0;
        bValue = b.visitLog?.length ?? 0;
      } else {
        aValue = a.profile?.[key];
        bValue = b.profile?.[key];
      }

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (order === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, []);

  /**
   * 유저 목록 로드 (일회성 쿼리)
   */
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await dbService.collection('user').get();
      const list = snap.docs
        .filter(doc => doc.data()?.profile)
        .map(doc => ({ ...doc.data(), docId: doc.id }));

      setUsers(applySort(list));
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, [applySort]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const sortUsers = (key) => {
    const order = sortConfig.key === key && sortConfig.order === 'asc' ? 'desc' : 'asc';
    const cfg = { key, order };
    sortConfigRef.current = cfg;
    setSortConfig(cfg);
    setUsers(prev => applySort(prev, cfg));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.order === 'asc' ? ' ↑' : ' ↓';
  };

  const formatLastVisit = (user) => {
    if (user.visitLog?.length > 0) {
      return moment(user.visitLog[user.visitLog.length - 1]).format('MM/DD hh:mm A');
    }
    if (user.profile?.signUpDate) {
      return moment(user.profile.signUpDate).format('MM/DD hh:mm A');
    }
    return '-';
  };

  return (
    <PageContainer w="100%">
      <Flex justify="space-between" align="center" mb="md">
        <div>
          <Title order={1}>Admin Page</Title>
          <Title order={2} fw={300}>
            number of users:{' '}
            <span style={{ fontWeight: 'bold' }}>{users.length}</span>
          </Title>
        </div>
        <Button onClick={loadUsers} loading={loading} variant="light">
          새로고침
        </Button>
      </Flex>

      {loading && users.length === 0 ? (
        <Flex justify="center" align="center" h={400}>
          <Loader size="lg" />
        </Flex>
      ) : (
        <div
          style={{
            position: 'relative',
            overflowY: 'auto',
            maxHeight: '800px',
            width: '100%',
          }}
        >
          <Table highlightOnHover withBorder withColumnBorders w="100%">
            <Table.Thead
              style={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 1,
              }}
            >
              <Table.Tr>
                <Table.Th>Profile</Table.Th>
                <Table.Th>Username</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>ID</Table.Th>
                <Table.Th
                  onClick={() => sortUsers('signUpDate')}
                  style={{ cursor: 'pointer' }}
                >
                  Sign Up Date{getSortIcon('signUpDate')}
                </Table.Th>
                <Table.Th
                  onClick={() => sortUsers('lastVisit')}
                  style={{ cursor: 'pointer' }}
                >
                  Last Visit{getSortIcon('lastVisit')}
                </Table.Th>
                <Table.Th
                  onClick={() => sortUsers('visitCount')}
                  style={{ cursor: 'pointer' }}
                >
                  Visit Count{getSortIcon('visitCount')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((user) => (
                <Table.Tr key={user.docId}>
                  <Table.Td>
                    <a
                      href={`https://threads.net/@${user?.profile?.username}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Image
                        w={32}
                        h={32}
                        radius="xl"
                        src={user?.profile?.threads_profile_picture_url}
                        alt="Profile"
                      />
                    </a>
                  </Table.Td>
                  <Table.Td fz={16}>{user?.profile?.username}</Table.Td>
                  <Table.Td fz={16}>{user?.profile?.name}</Table.Td>
                  <Table.Td fz={16}>{user?.profile?.id}</Table.Td>
                  <Table.Td fz={16}>
                    {user?.profile?.signUpDate
                      ? moment(user.profile.signUpDate).format('MM/DD/YYYY hh:mm A')
                      : '-'}
                  </Table.Td>
                  <Table.Td>
                    <Text fz={16}>{formatLastVisit(user)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz={16}>{user.visitLog?.length ?? 0}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}
    </PageContainer>
  );
};

export default Admin;
